import axios from 'axios';
import qs from 'qs';
import router from '@/router';
import { clearCasePreview, getAccessToken, getRefreshToken, removeToken, setToken } from '@/utils/auth';
import { TipsPop } from '@/utils/tool';

const baseURL = process.env.VUE_APP_BASE_API;

// 无需携带令牌的接口
const WHITE_LIST = ['/system/auth/login', '/system/auth/refresh-token'];

// yudao CommonResult 的成功码
const SUCCESS_CODE = 0;
// yudao 未登录 / 令牌失效，注意后端是以 HTTP 200 + code=401 的形式返回的
const UNAUTHORIZED_CODE = 401;

const EXPIRED_MSG = '未登录或登录已过期';
// 刷新令牌本身失效时后端返回的提示，这类消息不弹窗，直接静默登出（与管理端 portal-ui 保持一致）
const IGNORE_MSGS = ['无效的刷新令牌', '刷新令牌已过期'];

const service = axios.create({
  baseURL,
  timeout: 30000,
  withCredentials: false,
  paramsSerializer: params => qs.stringify(params, { allowDots: true })
});

// ========== 令牌刷新的单飞控制 ==========

let refreshing = false;
let pendingQueue = [];

function flushQueue(token) {
  const queue = pendingQueue;
  pendingQueue = [];
  queue.forEach(cb => cb(token));
}

// 令牌彻底失效：清本地状态并跳登录页
function forceLogout() {
  removeToken();
  clearCasePreview();
  const current = router.currentRoute;
  if (current && current.path !== '/login') {
    router
      .replace({
        path: '/login',
        query: { redirect: current.fullPath }
      })
      .catch(() => {});
  }
}

async function handleUnauthorized(config) {
  const refreshToken = getRefreshToken();
  // 没有刷新令牌，或换过新令牌重试后仍然 401，直接登出
  if (!refreshToken || config.__isRetry) {
    forceLogout();
    return Promise.reject(new Error(EXPIRED_MSG));
  }

  // 已经有请求在刷新令牌了，排队等它的结果
  if (refreshing) {
    return new Promise((resolve, reject) => {
      pendingQueue.push(ok => {
        if (!ok) {
          reject(new Error(EXPIRED_MSG));
          return;
        }
        // 重新走一遍实例，请求拦截器会带上刷新后的新令牌
        resolve(service({ ...config, __isRetry: true }));
      });
    });
  }

  refreshing = true;
  try {
    // 用裸 axios 发，避免走本实例的拦截器造成递归
    const res = await axios.post(baseURL + '/system/auth/refresh-token', null, {
      params: { refreshToken },
      timeout: 30000
    });
    const body = res.data;
    if (!body || body.code !== SUCCESS_CODE || !body.data) {
      throw new Error((body && body.msg) || '刷新令牌失败');
    }
    setToken(body.data);
  } catch (e) {
    // 注意：先复位 refreshing 再回放队列，否则回放中新产生的 401 会入队但无人再消费
    refreshing = false;
    flushQueue(false);
    forceLogout();
    return Promise.reject(new Error(EXPIRED_MSG));
  }
  refreshing = false;
  flushQueue(true);
  return service({ ...config, __isRetry: true });
}

// ========== 拦截器 ==========

service.interceptors.request.use(
  config => {
    const token = getAccessToken();
    const isWhite = WHITE_LIST.some(url => (config.url || '').indexOf(url) >= 0);
    if (token && !isWhite) {
      config.headers.Authorization = 'Bearer ' + token;
    }
    return config;
  },
  error => Promise.reject(error)
);

service.interceptors.response.use(
  response => {
    const { config, data } = response;
    // 非 CommonResult 结构（如二进制流），原样返回
    if (!data || typeof data.code === 'undefined') {
      return data;
    }
    if (data.code === SUCCESS_CODE) {
      return data.data;
    }
    if (data.code === UNAUTHORIZED_CODE) {
      return handleUnauthorized(config);
    }
    const message = data.msg || '请求失败，请稍后重试';
    // 刷新令牌失效类的提示不弹窗，直接登出
    if (IGNORE_MSGS.indexOf(message) >= 0) {
      forceLogout();
      return Promise.reject(new Error(EXPIRED_MSG));
    }
    if (!config.silent) {
      TipsPop({ message, type: 'error' });
    }
    // 必须 reject，否则调用方拿到的是一个已 resolve 的空值
    return Promise.reject(new Error(message));
  },
  error => {
    // 注意：超时 / 断网 / 请求被取消时 error.response 是 undefined
    const response = error && error.response;
    if (response && response.status === UNAUTHORIZED_CODE) {
      return handleUnauthorized(error.config || {});
    }

    let message = '网络异常，请稍后重试';
    if (response) {
      const body = response.data;
      if (body && body.msg) {
        message = body.msg;
      } else if (response.status === 403) {
        message = '没有权限访问该资源';
      } else if (response.status === 404) {
        message = '请求的资源不存在';
      } else if (response.status >= 500) {
        message = '服务异常，请稍后重试';
      } else {
        message = '请求失败（' + response.status + '）';
      }
    } else if (axios.isCancel && axios.isCancel(error)) {
      return Promise.reject(error);
    } else if (error && (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || ''))) {
      message = '请求超时，请稍后重试';
    }

    if (!(error && error.config && error.config.silent)) {
      TipsPop({ message, type: 'error' });
    }
    return Promise.reject(new Error(message));
  }
);

/**
 * get 请求
 * @param {String} url 请求地址
 * @param {Object} params 查询参数
 * @param {Object} config 额外的 axios 配置，支持 { silent: true } 关闭错误提示
 */
export function get(url, params, config) {
  return service.get(url, { params, ...config });
}

/**
 * post 请求，body 为 JSON
 */
export function post(url, data, config) {
  return service.post(url, data, config);
}

/**
 * post 请求，body 为 application/x-www-form-urlencoded
 */
export function postForm(url, data, config) {
  return service.post(url, qs.stringify(data), {
    ...config,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      ...((config && config.headers) || {})
    }
  });
}

export default service;
